import React from "react";
import { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import Calendar from "../Calendar";

import { apiGet, V2 } from "../../Api";
import { formatName, TZ } from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import HasRight from "../HasRight";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../viewer.css";
import DelayInput from "../DelayInput";
import withProps from "../withProps";

interface IAppointmentCalendarViewState {
    month: string;

    locations: any[];
    people: any[];
    appointmentTypes: any[];
    searchDateType: string;
    searchLocation: string;
    searchPerson: string;
    searchType: string;
    searchStatus: string;
    searchMode: string;

    displayBefore: DateTime;
    displayAfter: DateTime;

    events: { [date: number]: Array<{ title: string }>; };
    isLoading: boolean | string;

    [name: string]: any;
}

interface IAppointmentCalendarViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {
    mode: "all" | "hf"
}

class AppointmentCalendarView extends React.Component<IAppointmentCalendarViewProps, IAppointmentCalendarViewState> {
    constructor(props: Readonly<IAppointmentCalendarViewProps>) {
        super(props);

        const today = DateTime.local();

        let month = today.toFormat("yyyy-MM");
        let searchPerson = "";
        let searchLocation = ""; // !props.authContext.isInGroup(WellKnownGroups.FrontDesk) ? props.authContext.user.employee.locationId : "";
        let searchDateType = "for";
        let searchType = "";
        let searchStatus = "";
        let searchMode = props.mode;

        if (props.authContext.isInGroup(WellKnownGroups.UroTechnician)) {
            searchType = "uro";
        }
        else if (props.authContext.isInGroup(WellKnownGroups.PelletTechnician)) {
            searchType = "pellet";
        }

        if (props.authContext.isInGroup(WellKnownGroups.Marketer) && !props.authContext.isInGroup(WellKnownGroups.DefaultCalendarEveryone)) {
            searchPerson = "caller";
        }

        if (props.location.state) {
            // Show last shown month
            month = props.location.state.month || month;
            searchPerson = props.location.state.searchPerson;
            searchLocation = props.location.state.searchLocation;
            searchDateType = props.location.state.searchDateType;
            searchType = props.location.state.searchType;
            searchStatus = props.location.state.searchStatus;
            searchMode = props.location.state.searchMode;
        }

        if (searchPerson === "" && !props.authContext.hasRight("appt:readall")) {
            searchPerson = "caller";
        }

        this.state = {
            month,

            events: [],
            locations: [],
            people: [],
            appointmentTypes: [],

            searchDateType,
            searchLocation,
            searchPerson,
            searchType,
            searchStatus,
            searchMode,

            displayBefore: today.endOf("month"),
            displayAfter: today.startOf("month"),

            isLoading: true,
        };

        this.viewDayDetails = this.viewDayDetails.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.displayRangeChanged = this.displayRangeChanged.bind(this);
    }

    public async componentDidMount() {
        try {
            const [locations, appointmentTypes] = await Promise.all([
                apiGet<any[]>("locations"),
                V2.get<any[]>("appointments/types")
            ]);
            const marketers = this.props.authContext.hasRight("appt:readall")
                ? await apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`)
                : [];

            this.setState({
                locations,
                people: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),
                appointmentTypes
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }

    }

    public render() {
        return (
            <div className="viewer-root viewer-full full-height">
                <div className="filter-control-box">
                    <DelayInput type="month" timeout={300} className="fill-space" name="month" value={this.state.month} onChangeDelayed={this.handleElementChange} />
                    <select value={this.state.searchMode} name="searchMode" onChange={this.handleElementChange} disabled={this.state.isLoading === true || this.props.mode === "hf"}>
                        <option value="all">HF & Others</option>
                        <option value="hf">HF Only</option>
                        <option value="nonhf">Non-HF Only</option>
                    </select>
                    <select className="control-max-150px" value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="pendingreschedule">Pending Reschedule</option>
                        <option value="active">Active</option>
                        <option value="complete">Complete</option>
                        <option value="noshow">No Show</option>
                        <option value="canceled">Canceled</option>
                    </select>
                    <select className="control-max-150px" value={this.state.searchType} name="searchType" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Types</option>
                        {this.state.appointmentTypes.map(a => <option key={a.id} value={a.type}>{a.name}</option>)}
                    </select>
                    <select value={this.state.searchDateType} name="searchDateType" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="for">Scheduled For</option>
                        <option value="on">Created On</option>
                    </select>
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                    <select className="control-max-150px" value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <HasRight right="appt:readall">
                            <option value="">Everyone</option>
                        </HasRight>
                        <option value="caller">You</option>
                        <HasRight right="appt:readall">
                            {
                                this.state.people.map(a =>
                                    <option key={a.id} value={a.id}>{formatName(a.name)}</option>
                                )
                            }
                        </HasRight>
                    </select>
                </div>
                <div className="spinner-overlay-root full-height">
                    <Calendar className="full-height" year={parseInt(this.state.month.substr(0, 4), 10)} month={parseInt(this.state.month.substr(5, 2), 10)} highlightDate={new Date()} events={this.state.events} onDaySelected={this.viewDayDetails} onDisplayRangeChanged={this.displayRangeChanged} />
                    {
                        this.state.isLoading ? (
                            <div className="spinner-overlay">
                                {
                                    (typeof this.state.isLoading === "string") ?
                                        <span className="error-text-big">{this.state.isLoading}</span> :
                                        <div className="lds-dual-ring-big" />
                                }
                            </div>
                        ) : ""
                    }
                </div>
            </div>
        );
    }

    private async loadAppointments() {
        try {
            this.setState({
                events: {},
                isLoading: true,
            });

            // Only request the visible range's events
            const beginDate = this.state.displayAfter;
            const endDate = this.state.displayBefore.plus({ millisecond: 1 });

            const parameters = [
                `tz=${encodeURIComponent(TZ())}`,
                `after=${encodeURIComponent(beginDate.toISO())}`,
                `before=${encodeURIComponent(endDate.toISO())}`,
                `date_type=${this.state.searchDateType}`,
                `kind=${this.state.searchMode}`,
                "group=day,location"
            ];
            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
            }
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchType) {
                parameters.push(`type=${this.state.searchType}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }

            const appts = await apiGet<any>(`appointments/count?${parameters.join("&")}`);

            const bins: { [date: number]: Array<{ title: string, count: number, type?: string }>; } = {};

            const monthBase = DateTime.fromISO(this.state.month);
            for (const group of appts.groups) {
                const date = DateTime.fromISO(group.date).diff(monthBase, "days").days + 1;
                if (!bins[date]) {
                    bins[date] = [];
                }

                bins[date].push({
                    count: group.count,
                    title: group.count === 1 ? `1 ${group.name}` : `${group.count} ${group.name}`
                });
            }

            this.setState({
                events: bins,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                events: {},
                isLoading: "Could not load calendar"
            });
        }
    }

    private viewDayDetails(date: Date) {
        this.saveSearchState();

        const targetPath = this.props.mode === "hf"
            ? `/appointments/calendar/hf/${date.toISOString().substr(0, 10)}`
            : `/appointments/calendar/${date.toISOString().substr(0, 10)}`;

        this.props.history.push(targetPath, {
            searchLocation: this.state.searchLocation,
            searchPerson: this.state.searchPerson,
            searchDateType: this.state.searchDateType,
            searchType: this.state.searchType,
            searchStatus: this.state.searchStatus,
            searchMode: this.state.searchMode,
        });
    }

    private saveSearchState() {
        // Stash the currently displayed month so the user can come back to it
        const currentLocation = this.props.location;
        currentLocation.state = {
            month: this.state.month,

            searchDateType: this.state.searchDateType,
            searchLocation: this.state.searchLocation,
            searchPerson: this.state.searchPerson,
            searchType: this.state.searchType,
            searchStatus: this.state.searchStatus,
            searchMode: this.state.searchMode,
        };
        this.props.history.replace(currentLocation);
    }

    private handleElementChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if (name === "month" && !value) {
            return;
        }

        this.setState({
            [name]: value
        }, () => {
            this.saveSearchState();
            if (name !== "month") {
                this.loadAppointments();
            }
        });
    }

    private displayRangeChanged(minDate: Date, maxDate: Date) {
        this.setState({
            displayBefore: DateTime.fromJSDate(maxDate).toLocal(),
            displayAfter: DateTime.fromJSDate(minDate).toLocal()
        }, () => this.loadAppointments());
    }
}

export default withAuth(withTitle("Appointment Calendar", withProps(AppointmentCalendarView, { mode: "all" })));
export const AppointmentHfCalendarView = withAuth(withTitle("HF Appointment Calendar", withProps(AppointmentCalendarView, { mode: "hf" })));
